import React from 'react'

import AndroidHandler from 'utils/androidHandler'
import * as AppAuth from './useAppAuth'
import * as WebAuth from './useWebAuth'
import { isBrowser, isPathProtected } from './utils'
import { APP_LOGIN_STORAGE_KEY } from 'configs/auth'

import type { ProtectedRoutesComponentProps } from './types'
import type { Session } from 'types/auth'

export function useAuth() {
  const androidHandler = AndroidHandler()
  const Context = androidHandler.enabled
    ? AppAuth.AuthContext
    : WebAuth.AuthContext
  const context = React.useContext(Context)
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider')
  }
  return context
}

export function useAuthProvider(immediate: boolean) {
  const androidHandler = AndroidHandler()

  /* eslint-disable react-hooks/rules-of-hooks */
  // Android handler is not reactive, therefore it should be safe here
  // and instead we get a beautiful polymorphism!
  return androidHandler.enabled
    ? AppAuth.useAuthProvider(immediate)
    : WebAuth.useAuthProvider(immediate)
  /* eslint-enable react-hooks/rules-of-hooks */
}

export function AuthProvider(props) {
  const androidHandler = AndroidHandler()
  return androidHandler.enabled ? (
    <AppAuth.AuthProvider {...props} />
  ) : (
    <WebAuth.AuthProvider {...props} />
  )
}

export const login = (callbackUrl: string = '/pelatihan-mandiri') => {
  const parsedCallbackUrl = encodeURI(callbackUrl || '/pelatihan-mandiri')
  window.location.replace(
    `${window.location.origin}/login?from=${parsedCallbackUrl}`
  )
}

export function ProtectedRoutes({
  router,
  children,
}: ProtectedRoutesComponentProps) {
  const { session } = useAuth()
  const androidHandler = AndroidHandler()

  if (isBrowser() && session === null && isPathProtected(router)) {
    if (androidHandler.enabled) {
      androidHandler.requestLogin()
    } else {
      login(window.location.pathname + window.location.search)
    }
    return null
  }

  return children
}

export function setAuthToAPI(
  url: string,
  { accessToken, tokenType, refreshToken, expiryToken }: Session
): string {
  const urlObj = new URL(url)
  urlObj.searchParams.set('accessToken', accessToken)
  urlObj.searchParams.set('tokenType', tokenType)
  urlObj.searchParams.set('refreshToken', refreshToken)
  urlObj.searchParams.set('expiryToken', expiryToken)
  return urlObj.toString()
}

export function getSession(
  storageKey: string = APP_LOGIN_STORAGE_KEY
): Session | null {
  try {
    return JSON.parse(window.localStorage.getItem(storageKey))
  } catch {
    return null
  }
}
